import { Box, CounterBadge, StatusLabel, Text } from '@factorialco/deprecated-design-system'
import { AccessesWithTerminationDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ApproverWithBadge } from 'modules/approvals/components/ApproverWithBadge'
import {
  ApproverProps,
  badgeColors,
  stepStatus,
} from 'modules/approvals/lib/materializedApprovalFlows'

const hasAccessId = (value: ApproverProps['accessId']): value is string =>
  typeof value === 'string' && value.length > 0

interface Props {
  index: number
  status: string
  isCurrentStep: boolean
  updatedAt?: string | null
  overrided: boolean
  approvers: ApproverProps[]
}

export const Step = ({ index, status, isCurrentStep, updatedAt, overrided, approvers }: Props) => {
  const statusData = stepStatus(status, isCurrentStep, overrided)
  const badge = badgeColors(isCurrentStep, status)
  const accessIds = useUnsafeMemo(
    () => approvers.map((el) => el.accessId).filter((el) => hasAccessId(el)),
    [approvers]
  )
  const { data, error } = useQuery(AccessesWithTerminationDocument, {
    variables: { ids: accessIds },
  })

  const accesses = data?.apiCore.accessesConnection.nodes
  const loading = !data && !error

  return (
    <Box
      gap="s24"
      padding="s16"
      border={{
        top: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      flexDirection="row"
    >
      <Box alignItems="center">
        <CounterBadge count={index + 1} color={badge.color} textColor={badge.textColor} />
      </Box>
      <Box gap="s8" flexDirection="column">
        <StatusLabel type={statusData.type}>{statusData.label}</StatusLabel>
        <Box gap="s6" flexWrap="wrap" flexDirection="row">
          <Box gap="s12">
            <Box flexDirection="row" gap="s12" flexWrap="wrap">
              {loading && <Loading />}
              {!loading &&
                approvers.map((approver) => {
                  return (
                    <ApproverWithBadge
                      key={approver.id}
                      accessToApprove={accesses?.find((el) => el.id === approver.accessId)}
                      role={approver.role}
                      roleInitials={approver.roleInitials}
                      badge={approver.badge}
                      selectionCriteria={approver.selectionCriteria}
                    />
                  )
                })}
            </Box>
            {approvers.length > 1 && (
              <Box>
                <Text.Normal size="200">
                  {i18n.t('settings.approvals.approval_request_view.approval_history.anyofall')}
                </Text.Normal>
              </Box>
            )}
          </Box>
        </Box>
        {updatedAt && <Text.Normal size="200">{dateBackendDateTimeToUI(updatedAt)}</Text.Normal>}
      </Box>
    </Box>
  )
}
